
/**
 * Step 4: 文案预览和编辑 (优化版 - 统一视图)
 */

import React from 'react';
import { Card, Button, Space, Input, Empty, Spin, Typography, Modal } from 'antd';
import { SaveOutlined, FileTextOutlined } from '@ant-design/icons';
import type { GenerateCopyResult } from '../types';
import { UnifiedCopyView } from './common';
import { useCopyGenerationStep } from './hooks/useCopyGenerationStep';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface CopyGenerationStepProps {
    result: GenerateCopyResult | null;
    keywords?: string[];  // 用于SEO分析
    loading?: boolean;
    onSave: (notes?: string) => Promise<void>;
    onBack: () => void;
    onRegenerate?: () => void;
    onRegenerateFABE?: (sellingPoint: string) => Promise<void>;
    saving?: boolean;
}

export const CopyGenerationStep: React.FC<CopyGenerationStepProps> = ({
    result,
    keywords = [],
    loading,
    onSave,
    onBack,
    onRegenerate,
    onRegenerateFABE,
    saving,
}) => {
    const {
        saveModalVisible,
        setSaveModalVisible,
        saveNotes,
        setSaveNotes,
        regeneratingFABE,
        handleSave,
        handleRegenerateFABE
    } = useCopyGenerationStep({
        onSave,
        onRegenerateFABE
    });

    // 加载状态
    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 96 }}>
                <Spin size="large" />
                <div style={{ marginTop: 24 }}>
                    <Title level={4}>🤖 AI 正在生成文案...</Title>
                    <Text type="secondary">
                        结合目标人群和使用场景，运用FABE方法论和蓝/绿剧本逻辑...
                    </Text>
                </div>
            </div>
        );
    }

    // 无结果状态
    if (!result) {
        return (
            <div className="copy-generation-step">
                <Card bordered={false}>
                    <Empty description="暂无文案结果，可能是由于刷新页面导致未保存的文案丢失">
                        <Button type="primary" onClick={onBack}>
                            返回上一步重新生成
                        </Button>
                    </Empty>
                </Card>
            </div>
        );
    }

    return (
        <div className="copy-generation-step">
            <Card
                title={
                    <Space>
                        <FileTextOutlined />
                        文案结果
                    </Space>
                }
                bordered={false}
                extra={
                    <Space>
                        {onRegenerate && (
                            <Button onClick={onRegenerate}>
                                重新生成
                            </Button>
                        )}
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={() => setSaveModalVisible(true)}
                            loading={saving}
                        >
                            保存版本
                        </Button>
                    </Space>
                }
            >
                {/* 使用统一视图替代Tab */}
                <UnifiedCopyView
                    result={result}
                    keywords={keywords}
                    onRegenerateFABE={onRegenerateFABE ? handleRegenerateFABE : undefined}
                    regeneratingFABE={regeneratingFABE}
                />
            </Card>

            {/* 底部操作栏 */}
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
                <Button size="large" onClick={onBack}>
                    上一步
                </Button>
                <Button
                    type="primary"
                    size="large"
                    icon={<SaveOutlined />}
                    onClick={() => setSaveModalVisible(true)}
                    loading={saving}
                >
                    保存到历史版本
                </Button>
            </div>

            {/* 保存版本弹窗 */}
            <Modal
                title="保存文案版本"
                open={saveModalVisible}
                onOk={handleSave}
                onCancel={() => setSaveModalVisible(false)}
                confirmLoading={saving}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">
                        保存后可在历史版本中查看和恢复此版本的文案
                    </Text>
                </div>
                <TextArea
                    value={saveNotes}
                    onChange={e => setSaveNotes(e.target.value)}
                    placeholder="添加版本备注（可选）"
                    autoSize={{ minRows: 2, maxRows: 4 }}
                />
            </Modal>
        </div>
    );
};

export default CopyGenerationStep;
